import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { LookupSelect } from "@/components/app/LookupSelect";

const LOOKUPS = ["warehouse_type", "region"];

const FIELDS = [["name", "Warehouse name *", 2], ["warehouse_type", "Warehouse type"], ["region", "Region"], ["address", "Address *", 2], ["city", "City"], ["state", "State *"], ["pincode", "Pincode"], ["gstin", "GSTIN of this state *"], ["phone", "Phone"], ["email", "Email"], ["remarks", "Remarks", 2]];

function WarehouseDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState({ ...Object.fromEntries(FIELDS.map(([k]) => [k, value?.[k] || ""])), is_default: !!value?.is_default, active: value?.active ?? true });
  const save = async () => {
    try {
      const r = value?.id ? await api.put(`/settings/warehouses/${value.id}`, f) : await api.post("/settings/warehouses", f);
      toast.success(`${r.data.code} saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="warehouse-dialog">
        <DialogHeader><DialogTitle>{value?.id ? `Edit ${value.code}` : "New dispatch warehouse"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">{FIELDS.map(([k, l, span]) => <div key={k} className={span ? "col-span-2" : ""}><Label>{l}</Label>
          {LOOKUPS.includes(k) ? <LookupSelect type={k} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`warehouse-${k}-input`} />
            : <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: k === "gstin" ? e.target.value.toUpperCase() : e.target.value })} data-testid={`warehouse-${k}-input`} />}</div>)}</div>
        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2"><Switch checked={f.is_default} onCheckedChange={(v) => setF({ ...f, is_default: v })} data-testid="warehouse-default-switch" />Default for challans</label>
          <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="warehouse-active-switch" />Active</label>
        </div>
        <p className="text-xs text-slate-500">The GSTIN must belong to the warehouse's state. It prints as the consigner on Delivery Challans and decides CGST+SGST vs IGST.</p>
        <DialogFooter><Button onClick={save} data-testid="warehouse-save-btn">Save warehouse</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dispatch warehouses (consigner) master; admins edit, everyone else sees the list.
export function Warehouses() {
  const { can } = useMe();
  const admin = can("admin.settings");
  const [edit, setEdit] = useState(null);
  const { data, reload } = useFetch("/settings/warehouses");
  const cols = [
    { key: "code", label: "Code", mono: true }, { key: "name", label: "Warehouse" }, { key: "state", label: "State", exp: (w) => `${w.state} (${w.state_code})`, render: (w) => `${w.state} (${w.state_code})` },
    { key: "gstin", label: "GSTIN", mono: true }, { key: "warehouse_type", label: "Type" }, { key: "region", label: "Region" }, { key: "city", label: "City" }, { key: "address", label: "Address" },
    { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "remarks", label: "Remarks" },
    { key: "is_default", label: "Default", exp: (w) => (w.is_default ? "Yes" : ""), render: (w) => (w.is_default ? "Yes" : "") },
    { key: "active", label: "Active", exp: (w) => (w.active ? "Yes" : "No"), render: (w) => (w.active ? "Yes" : "No") },
    { key: "edit", label: "", exp: false, render: (w) => admin && <Button size="sm" variant="ghost" onClick={() => setEdit(w)} data-testid={`warehouse-edit-${w.code}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <div className="mt-2" data-testid="warehouses-section">
      <div className="flex items-center gap-2 mb-3"><h2 className="text-base font-semibold">Dispatch warehouses (consigner on Delivery Challans)</h2>
        {admin && <Button className="ml-auto" onClick={() => setEdit({})} data-testid="new-warehouse-btn"><Plus className="h-4 w-4 mr-1" />New warehouse</Button>}</div>
      <DataTable testid="warehouses-table" columns={cols} rows={data} exportName="Warehouses" exportPerm="logistics.export" empty="No warehouses yet — admins add each dispatch location with its state GSTIN" />
      {edit && <WarehouseDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
